import { BrandedQr } from '../ui/brandcode';
import { C, Explainer, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps, type Unit } from './engine';
import { fitMono } from './unit';
import { accentOf, markOf } from './maker';


const T = { rows: 300, pick: 1300, wire: 1650, print: 2050, printed: 3050, fly: 3400, stick: 4150, live: 4600 };

// Label geometry: printed under the printer, then flown onto the plate.
const LBL = { w: 118, h: 140 };
const AT_PRINTER = { x: 331, y: 70 };
const AT_PLATE = { x: 252, y: 247, s: 0.6 };

function Label({ u }: { u: Unit }) {
  return (
    <div style={{ width: LBL.w, height: LBL.h, background: '#fff', borderRadius: 7, boxShadow: '0 0 0 1px #DCDBEA', padding: '9px 10px 0', textAlign: 'center', color: C.indigo }}>
      <div className="xp-row" style={{ justifyContent: 'center' }}>
        <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.16em', whiteSpace: 'nowrap', color: accentOf(u) }}>{u.brand.toUpperCase()}</span>
      </div>
      <div style={{ margin: '7px auto 6px', width: 66 }}>
        <BrandedQr accent={accentOf(u)} mark={markOf(u)} seed={u.serial} size={66} />
      </div>
      <div className="xp-mono" style={{ fontSize: fitMono(u.serial, 104, 9.5), fontWeight: 600, letterSpacing: '.02em', whiteSpace: 'nowrap' }}>{u.serial}</div>
      <div className="xp-mono" style={{ fontSize: 7.5, color: C.muted, marginTop: 1 }}>LOT {u.lot}</div>
    </div>
  );
}

function TagScene({ t, tone, u }: SceneProps) {
  const ROWS: [string, string, string][] = [[u.title.length > 18 ? u.model : u.title, u.serial, u.lot], ...u.parts.slice(0, 3)];
  const rowMono = Math.min(9.5, ...ROWS.map((r) => fitMono(r[1], 92, 9.5)));
  const dark = tone === 'dark';
  const at = (ms: number) => t >= ms;
  const flying = at(T.fly);
  const landed = at(T.fly + 80);
  const stuck = at(T.stick);
  return (
    <>
      {/* Machine side panel with the serial plate */}
      <div className="xp-abs" style={{ left: 14, top: 222, width: 452, height: 160, borderRadius: 16, background: dark ? '#3B3E47' : '#34373F', boxShadow: dark ? 'inset 0 0 0 1px rgba(255,255,255,.08)' : 'none' }}>
        <div className="xp-abs" style={{ left: 0, right: 0, top: 0, height: 1, background: 'rgba(255,255,255,.22)', borderRadius: 16 }} />
        {[18, 432].map((x) => (
          <span key={x} className="xp-abs" style={{ left: x, top: 16, width: 8, height: 8, borderRadius: 9, background: '#24262C', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.25)' }} />
        ))}
        <div className="xp-abs" style={{ left: 30, top: 18, width: 300, height: 104, borderRadius: 6, background: '#DCDEE4', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12), inset 0 1px 0 rgba(255,255,255,.7), 0 2px 0 rgba(0,0,0,.18)', color: '#3A3D48' }}>
          {[
            [6, 6],
            [286, 6],
            [6, 90],
            [286, 90],
          ].map(([x, y]) => (
            <span key={`${x}${y}`} className="xp-abs" style={{ left: x, top: y, width: 7, height: 7, borderRadius: 7, background: '#B5B8C1', boxShadow: 'inset 0 -1px 0 rgba(0,0,0,.2)' }} />
          ))}
          <div className="xp-abs" style={{ left: 20, top: 14, fontSize: 13, fontWeight: 800, letterSpacing: '.2em', whiteSpace: 'nowrap' }}>{u.brand.toUpperCase()}</div>
          <div className="xp-abs xp-mono" style={{ left: 20, top: 38, fontSize: 9, lineHeight: '15px', letterSpacing: '.04em' }}>
            <div>
              <span style={{ opacity: 0.55 }}>MODEL&nbsp;&nbsp;</span>{u.plateModel}
            </div>
            <div>
              <span style={{ opacity: 0.55 }}>SERIAL </span>{u.serial}
            </div>
            <div>
              <span style={{ opacity: 0.55 }}>YEAR&nbsp;&nbsp;&nbsp;</span>2026
            </div>
          </div>
          {/* the empty spot the label will occupy */}
          <div className="xp-abs xp-f" style={{ left: 214, top: 10, width: 72, height: 85, borderRadius: 5, border: '1px dashed rgba(58,61,72,.35)', opacity: stuck ? 0 : 1 }} />
        </div>
      </div>

      {/* ERP window */}
      <div className="xp-abs xp-card" style={{ left: 16, top: 18, width: 290, overflow: 'hidden' }}>
        <div className="xp-row" style={{ height: 28, padding: '0 12px', gap: 5, background: C.alt, borderBottom: `1px solid ${C.line}` }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ width: 7, height: 7, borderRadius: 7, background: '#DCDBEA' }} />
          ))}
          <span className="xp-cap" style={{ marginLeft: 8, color: C.muted, fontSize: 9 }}>
            Your ERP · Shipments
          </span>
        </div>
        <div className="xp-row" style={{ padding: '10px 12px 8px', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '-.01em', whiteSpace: 'nowrap' }}>Packing slip PS-30418</span>
          <span className="xp-mono" style={{ fontSize: 8.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 104 }}>
            {u.shipTo.toUpperCase()}
          </span>
        </div>
        <div className="xp-row xp-cap" style={{ padding: '0 12px 5px', fontSize: 8, color: C.muted, borderBottom: `1px solid ${C.line}` }}>
          <span style={{ width: 112 }}>Part</span>
          <span style={{ width: 94 }}>Serial</span>
          <span>Lot</span>
        </div>
        {ROWS.map((r, i) => {
          const on = at(T.rows + i * 110);
          const picked = i === 0 && at(T.pick);
          return (
            <div
              key={r[1]}
              className="xp-row xp-a"
              style={{
                ...rise(on, 0, 6),
                height: 26,
                padding: '0 12px',
                fontSize: 10.5,
                borderBottom: `1px solid ${C.line}`,
                background: picked ? 'rgba(63,174,108,.10)' : 'transparent',
                boxShadow: picked ? `inset 2px 0 0 ${C.emerald}` : 'none',
                transition: 'transform .5s var(--xe), opacity .2s var(--xe), background .2s',
              }}
            >
              <span style={{ width: 112, fontWeight: i === 0 ? 600 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>{r[0]}</span>
              <span className="xp-mono" style={{ width: 94, fontSize: rowMono, letterSpacing: '.01em', whiteSpace: 'nowrap' }}>
                {r[1]}
              </span>
              <span className="xp-mono" style={{ fontSize: rowMono, letterSpacing: '.01em', color: C.muted, whiteSpace: 'nowrap' }}>
                {r[2]}
              </span>
            </div>
          );
        })}
      </div>

      {/* wire from the picked row to the printer */}
      <svg className="xp-abs" style={{ left: 0, top: 0, overflow: 'visible' }} width={480} height={360}>
        <path d="M306 117 C 318 117, 312 48, 324 48" fill="none" stroke={dark ? 'rgba(207,247,16,.7)' : C.emerald} strokeWidth="1.25" strokeDasharray="110" strokeDashoffset={at(T.wire) ? 0 : 110} style={{ transition: 'stroke-dashoffset .45s var(--xe)' }} />
        <circle cx="306" cy="117" r="2.5" fill={dark ? C.lime : C.emerald} className="xp-f" style={{ opacity: at(T.wire) ? 1 : 0 }} />
      </svg>

      {/* Printer */}
      <div className="xp-abs" style={{ left: 322, top: 22, width: 136, height: 50, borderRadius: 12, background: dark ? '#0E0B2B' : C.deep, boxShadow: dark ? '0 0 0 1px rgba(255,255,255,.14)' : '0 14px 28px -16px rgba(24,19,67,.6)' }}>
        <span className="xp-cap" style={{ position: 'absolute', left: 14, top: 12, fontSize: 8.5, color: 'rgba(255,255,255,.7)' }}>
          Label printer
        </span>
        <span className="xp-abs" style={{ right: 14, top: 14, width: 7, height: 7, borderRadius: 7, background: at(T.print) && !at(T.printed) ? C.lime : at(T.printed) ? C.green : 'rgba(255,255,255,.25)', animation: at(T.print) && !at(T.printed) ? 'xp-blink .4s linear infinite' : 'none' }} />
        <span className="xp-abs" style={{ left: 10, right: 10, bottom: 9, height: 4, borderRadius: 4, background: '#05041a' }} />
      </div>

      {/* Label emerging from the slot (clipped) */}
      <div className="xp-abs" style={{ left: AT_PRINTER.x, top: AT_PRINTER.y - 6, width: LBL.w, height: LBL.h + 10, overflow: 'hidden', visibility: flying ? 'hidden' : 'visible' }}>
        <div
          style={{
            transform: at(T.print) ? 'translateY(6px)' : `translateY(${-LBL.h}px)`,
            transition: `transform ${T.printed - T.print}ms cubic-bezier(.45,.05,.55,.95)`,
          }}
        >
          <Label u={u} />
        </div>
      </div>

      {/* Label in flight / applied */}
      <div
        className="xp-abs"
        style={{
          left: 0,
          top: 0,
          visibility: flying ? 'visible' : 'hidden',
          transformOrigin: '0 0',
          transform: landed ? `translate(${AT_PLATE.x}px, ${AT_PLATE.y}px) scale(${stuck ? AT_PLATE.s : AT_PLATE.s + 0.04}) rotate(${stuck ? 0 : -2}deg)` : `translate(${AT_PRINTER.x}px, ${AT_PRINTER.y}px) scale(1)`,
          transition: stuck ? `transform .16s ${'cubic-bezier(.3,0,.2,1)'}` : `transform .7s var(--xe)`,
          filter: stuck ? 'drop-shadow(0 1px 1px rgba(0,0,0,.25))' : 'drop-shadow(0 14px 16px rgba(24,19,67,.35))',
          zIndex: 5,
        }}
      >
        <Label u={u} />
      </div>

      {/* Result */}
      <div className="xp-abs xp-card xp-a" style={{ ...rise(at(T.live)), left: 330, top: 104, width: 132, padding: '10px 12px', zIndex: 6 }}>
        <div className="xp-row" style={{ gap: 7 }}>
          <span className="xp-row" style={{ width: 18, height: 18, borderRadius: 18, background: C.emerald, justifyContent: 'center' }}>
            <Tick on={at(T.live + 120)} size={12} />
          </span>
          <span style={{ fontSize: 11.5, fontWeight: 700 }}>Unit tagged</span>
        </div>
        <div style={{ fontSize: 9.5, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>Scans resolve to this serial, on your domain.</div>
      </div>

    </>
  );
}

export const tagDef: SceneDef = {
  name: 'Tag',
  label: (u) => `A packing slip row from your ERP prints as a QR label, which is applied to the serial plate of a ${u.brand} ${u.title}. The unit is now tagged.`,
  W: 480,
  H: 360,
  duration: 8000,
  end: 7400,
  steps: [
    { at: 0, label: 'Rows from your ERP' },
    { at: T.pick, label: 'Label prints' },
    { at: T.fly, label: 'Onto the serial plate' },
    { at: T.live, label: 'Unit tagged' },
  ],
  Scene: TagScene,
};

export const TagExplainer = (p: ExplainerProps) => <Explainer def={tagDef} {...p} />;
